import { doc, setDoc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { TimeTag } from '../components/TimeTagSettingsModal';

export class TimeTagService {
  private static getCollectionPath(userId: string, classId: string): string {
    return `users/${userId}/classes/${classId}/setCollection`;
  }

  private static getDocumentPath(userId: string, classId: string): string {
    return `${this.getCollectionPath(userId, classId)}/generalSet`;
  }

  // 타임 태그 목록 조회
  static async getTimeTags(userId: string, classId: string): Promise<TimeTag[]> {
    try {
      const generalSetRef = doc(db, this.getDocumentPath(userId, classId));
      const docSnap = await getDoc(generalSetRef);
      
      if (!docSnap.exists()) {
        return [];
      }
      
      const data = docSnap.data();
      const timeTags = data.timeTags || [];
      
      // startTime 기준으로 정렬
      return timeTags.sort((a: TimeTag, b: TimeTag) => a.startTime.localeCompare(b.startTime));
    } catch (error) {
      console.error('타임 태그 조회 실패:', error);
      throw error;
    }
  }

  // 타임 태그 생성
  static async createTimeTag(userId: string, classId: string, timeTag: Omit<TimeTag, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const generalSetRef = doc(db, this.getDocumentPath(userId, classId));
      const docSnap = await getDoc(generalSetRef);
      
      const newTimeTag: TimeTag = {
        ...timeTag,
        id: `TI_${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      if (!docSnap.exists()) {
        // 문서가 없으면 새로 생성
        await setDoc(generalSetRef, {
          timeTags: [newTimeTag],
          createdAt: new Date(),
          updatedAt: new Date()
        });
      } else {
        // 기존 문서에 타임태그 추가
        await updateDoc(generalSetRef, {
          timeTags: arrayUnion(newTimeTag),
          updatedAt: new Date()
        });
      }
      
      console.log('타임 태그 생성 완료:', newTimeTag.id);
      return newTimeTag.id;
    } catch (error) {
      console.error('타임 태그 생성 실패:', error);
      throw error;
    }
  }

  // 타임 태그 수정
  static async updateTimeTag(userId: string, classId: string, timeTagId: string, updates: Partial<Omit<TimeTag, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const generalSetRef = doc(db, this.getDocumentPath(userId, classId));
      const docSnap = await getDoc(generalSetRef);
      
      if (!docSnap.exists()) {
        throw new Error('GeneralSet 문서를 찾을 수 없습니다.');
      }
      
      const data = docSnap.data();
      const timeTags = data.timeTags || [];
      
      // 해당 타임태그 찾아서 수정
      const updatedTimeTags = timeTags.map((tag: TimeTag) => 
        tag.id === timeTagId 
          ? { ...tag, ...updates, updatedAt: new Date() }
          : tag
      );
      
      await updateDoc(generalSetRef, {
        timeTags: updatedTimeTags,
        updatedAt: new Date()
      });
      
      console.log('타임 태그 수정 완료:', timeTagId);
    } catch (error) {
      console.error('타임 태그 수정 실패:', error);
      throw error;
    }
  }

  // 타임 태그 삭제
  static async deleteTimeTag(userId: string, classId: string, timeTagId: string): Promise<void> {
    try {
      const generalSetRef = doc(db, this.getDocumentPath(userId, classId));
      const docSnap = await getDoc(generalSetRef);
      
      if (!docSnap.exists()) {
        throw new Error('GeneralSet 문서를 찾을 수 없습니다.');
      }
      
      const data = docSnap.data();
      const timeTags = data.timeTags || [];
      
      // 해당 타임태그 제거
      const updatedTimeTags = timeTags.filter((tag: TimeTag) => tag.id !== timeTagId);
      
      await updateDoc(generalSetRef, {
        timeTags: updatedTimeTags,
        updatedAt: new Date()
      });
      
      console.log('타임 태그 삭제 완료:', timeTagId);
    } catch (error) {
      console.error('타임 태그 삭제 실패:', error);
      throw error;
    }
  }

  // 타임 태그 일괄 저장 (기존 태그들 삭제 후 새로 생성)
  static async saveTimeTags(userId: string, classId: string, timeTags: TimeTag[]): Promise<void> {
    try {
      const generalSetRef = doc(db, this.getDocumentPath(userId, classId));
      
      // 타임태그 배열을 직접 저장
      await setDoc(generalSetRef, {
        timeTags: timeTags,
        createdAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });
      
      console.log('타임 태그 일괄 저장 완료');
    } catch (error) {
      console.error('타임 태그 일괄 저장 실패:', error);
      throw error;
    }
  }

  // 현재 시간에 해당하는 타임 태그 찾기
  static async getCurrentTimeTag(userId: string, classId: string): Promise<TimeTag | null> {
    try {
      const timeTags = await this.getTimeTags(userId, classId);
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      // 현재 시간이 포함되는 태그 찾기
      const currentTag = timeTags.find(tag => 
        currentTime >= tag.startTime && currentTime <= tag.endTime
      );
      
      return currentTag || null;
    } catch (error) {
      console.error('현재 타임 태그 조회 실패:', error);
      throw error;
    }
  }

  // 특정 시간에 해당하는 타임 태그 찾기
  static async getTimeTagByTime(userId: string, classId: string, time: string): Promise<TimeTag | null> {
    try {
      const timeTags = await this.getTimeTags(userId, classId);
      
      // 지정된 시간이 포함되는 태그 찾기
      const targetTag = timeTags.find(tag => 
        time >= tag.startTime && time <= tag.endTime
      );
      
      return targetTag || null;
    } catch (error) {
      console.error('시간별 타임 태그 조회 실패:', error);
      throw error;
    }
  }
}
